import { useTranslation } from '@govard/i18n-core/react';
// Смена пароля по ссылке из письма.
//
// После успеха все сессии закрыты сервером, поэтому отсюда всегда идём на вход,
// а не в приложение.
import { useState, type ReactElement } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Card, Form, Input, Typography } from 'antd';

import { ApiError } from '../../../shared/api-client/index.js';
import { resetPassword } from '../api/account.client.js';
import { AuthMark } from '../components/AuthMark.js';
import { LanguageSwitcher } from '../../../components/layout/LanguageSwitcher.js';

const { Title, Text } = Typography;

interface FormValues {
  password: string;
  repeat: string;
}

export function ResetPasswordPage(): ReactElement {
  const { t } = useTranslation();
  const { token } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm<FormValues>();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function handleSubmit(values: FormValues): Promise<void> {
    if (!token) return;
    setSubmitting(true);
    setError(null);
    try {
      await resetPassword(token, values.password);
      setDone(true);
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 400
          ? t('crm.auth.reset.expired')
          : t('crm.auth.reset.error'),
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main
      className="relative flex min-h-screen items-center justify-center p-6"
      style={{ backgroundColor: 'var(--ant-color-bg-layout)' }}
    >
      <div className="absolute right-6 top-6" data-testid="public-language-switcher-position">
        <LanguageSwitcher />
      </div>
      <Card style={{ maxWidth: 440, width: '100%' }}>
        <div className="mb-4 flex items-center gap-3">
          <AuthMark />
          <Title level={4} style={{ margin: 0 }}>
            {t('crm.auth.reset.title')}
          </Title>
        </div>

        {done ? (
          <div className="flex flex-col gap-3">
            <Alert
              type="success"
              showIcon
              title={t('crm.auth.reset.success_title')}
              description={t('crm.auth.reset.success_description')}
            />
            <Button type="primary" onClick={() => navigate('/login', { replace: true })}>
              {t('crm.auth.reset.to_login')}
            </Button>
          </div>
        ) : (
          <Form<FormValues> form={form} layout="vertical" onFinish={(v) => void handleSubmit(v)}>
            <Text type="secondary" style={{ fontSize: 13 }}>
              {t('crm.auth.reset.description')}
            </Text>

            {error ? (
              <Alert
                type="error"
                showIcon
                className="mt-3"
                title={error}
                description={<Link to="/forgot-password">{t('crm.auth.reset.new_link')}</Link>}
              />
            ) : null}

            <Form.Item
              name="password"
              label={t('crm.auth.reset.password')}
              className="mt-3"
              rules={[
                { required: true, message: t('crm.auth.reset.password_required') },
                { min: 8, message: t('crm.auth.reset.password_min') },
              ]}
            >
              <Input.Password autoFocus maxLength={200} />
            </Form.Item>

            <Form.Item
              name="repeat"
              label={t('crm.auth.reset.repeat')}
              dependencies={['password']}
              rules={[
                { required: true, message: t('crm.auth.reset.repeat_required') },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    // Опечатка в новом пароле обнаружилась бы только при входе,
                    // когда сменить его повторно уже нечем — ссылка одноразовая.
                    return !value || getFieldValue('password') === value
                      ? Promise.resolve()
                      : Promise.reject(new Error(t('crm.auth.reset.mismatch')));
                  },
                }),
              ]}
            >
              <Input.Password maxLength={200} />
            </Form.Item>

            <Button type="primary" htmlType="submit" loading={submitting}>
              {t('crm.auth.reset.submit')}
            </Button>
          </Form>
        )}
      </Card>
    </main>
  );
}
